import { SearchFilterTag } from '@/components';
import { useAccountingTypes } from '@/features/master-table/hooks';
import type { AnalysisSearchFormType } from '@/features/search/_models/analysis-search';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import type { SharedSearchFormType } from '@/features/search/_models/shared-search';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { ITEM_SPLITTER_COMMA } from '@/utils';
import type { analysisTargetEnum } from '@/utils/enum';
import type { Dayjs } from 'dayjs';
import { type FC, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

export const AccountingTypeIdsTag: FC = () => {
  const { t } = useTranslation();
  const { type, setInputCascaderAndAccountingLabels } =
    useProjectsSearchStore();
  const { control: sharedSearchControl, setValue: setSharedSearchValue } =
    useFormContext<SharedSearchFormType>();
  const { control: projectSearchControl } =
    useFormContext<ProjectSearchFormType>();
  const { control: analysisSearchControl } =
    useFormContext<AnalysisSearchFormType>();

  const [sheetType, accountingTypeIds] = useWatch({
    control: sharedSearchControl,
    name: ['sheetType', 'accountingTypeIds'],
  });
  // 分析側では呼ばないためundefinedになる
  const fiscalYear: Dayjs | undefined = useWatch({
    control: projectSearchControl,
    name: 'fiscalYear',
  });

  // project側では呼ばないためundefinedになる
  const fiscalYearRange = useWatch({
    control: analysisSearchControl,
    name: 'fiscalYearRange',
  });

  // project側では呼ばないためundefinedになる
  const analysisTarget: z.infer<typeof analysisTargetEnum> | undefined =
    useWatch({
      control: analysisSearchControl,
      name: 'analysisTarget',
    });

  const startYearAccountingTypeOptions = useAccountingTypes({
    params: {
      referenceFiscalYear:
        type === 'analysis'
          ? fiscalYearRange?.at(0)?.year().toString()
          : fiscalYear?.year().toString(),
    },
    enabled: !!fiscalYear,
  });
  const endYearAccountingTypeOptions = useAccountingTypes({
    params: { referenceFiscalYear: fiscalYearRange?.at(1)?.year().toString() },
    enabled:
      type === 'analysis' &&
      analysisTarget === 'expenditure' &&
      !!fiscalYearRange,
  });

  const accountingTypeNames = useMemo<string[]>(() => {
    const options =
      (type !== 'analysis'
        ? startYearAccountingTypeOptions.data
        : [
            ...(startYearAccountingTypeOptions.data ?? []),
            ...(endYearAccountingTypeOptions.data ?? []),
          ]) ?? [];
    return (
      accountingTypeIds?.flatMap(
        (id) => options.find((d) => d.id === id)?.name ?? [],
      ) ?? []
    );
  }, [
    startYearAccountingTypeOptions.data,
    endYearAccountingTypeOptions.data,
    accountingTypeIds,
    type,
  ]);

  useEffect(() => {
    setInputCascaderAndAccountingLabels((prev) => ({
      ...prev,
      accountingTypeIds: accountingTypeNames?.length ? accountingTypeNames : [],
    }));
  }, [accountingTypeNames, setInputCascaderAndAccountingLabels]);

  if (sheetType !== 'RS' || !accountingTypeIds?.length) {
    return null;
  }

  return (
    <SearchFilterTag
      closable={true}
      label={`${t('accounting-name')}: ${accountingTypeNames?.join(ITEM_SPLITTER_COMMA)}`}
      onClose={() => {
        setSharedSearchValue('accountingTypeIds', null);
      }}
    />
  );
};
